import { Fragment, type Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import { ReplaceAroundStep } from '@atlaskit/editor-prosemirror/transform';

import type { DiffContext } from './types';
import { contentEqIgnoring, nodeEqIgnoring } from './utils';

/**
 * Tries to express the transformation `oldNode → newNode` as a single
 * `ReplaceAroundStep` that preserves the inner content as a gap. Returns null
 * when none of the supported patterns apply.
 *
 * Why bother: the gap content is transferred verbatim by PM, including all
 * marks. So an inline comment on text that's about to be re-wrapped (e.g.
 * the user adds `>` to make a paragraph into a blockquote) survives without
 * any explicit mark-inheritance work. A fallback `ReplaceStep` would replace
 * the whole node and lose those marks.
 *
 * Three supported patterns:
 *
 *  1. **Type change, same content.** `p("Hi") → h1("Hi")`,
 *     `p("Hi") → code_block("Hi")`. The gap is the inline content.
 *
 *  2. **Wrap.** `p("Hi") → blockquote(p("Hi"))`. The gap is the entire old
 *     node; the new node provides an empty outer shell.
 *
 *  3. **Unwrap.** `blockquote(p("Hi")) → p("Hi")`. The gap is the old node's
 *     only child; the slice is empty.
 *
 * Cases NOT handled (caller falls back to `ReplaceStep`):
 *  - type change with content edits in the same pass (`p("Hi") → h1("HI")`)
 *  - multi-level wrap / unwrap (`p → blockquote(blockquote(p))`)
 *  - list-item lift / sink / split / join
 *
 * `pos` is the position of `oldNode` in the parent doc.
 */
export function tryReplaceAround(
	oldNode: PMNode,
	newNode: PMNode,
	pos: number,
	ctx: DiffContext,
): ReplaceAroundStep | null {
	if (nodeEqIgnoring(oldNode, newNode, ctx.ignoreMarks)) return null;

	// ---------------------------------------------------------------------------
	// Case 1: same content, different shell (type and/or attrs+marks)
	// ---------------------------------------------------------------------------
	// Same type with different attrs is handled by AttrStep elsewhere, so this
	// pattern is meaningful only when the type itself changes.
	if (oldNode.type !== newNode.type && contentEqIgnoring(oldNode, newNode, ctx.ignoreMarks)) {
		return wrapStep(
			/* from   */ pos,
			/* to     */ pos + oldNode.nodeSize,
			/* gapFrom*/ pos + 1,
			/* gapTo  */ pos + 1 + oldNode.content.size,
			/* shell  */ newNode.type.create(newNode.attrs, null, newNode.marks),
			/* insert */ 1,
		);
	}

	// ---------------------------------------------------------------------------
	// Case 2: wrap (newNode has a single child equal to oldNode, modulo ignored marks)
	// ---------------------------------------------------------------------------
	if (
		newNode.childCount === 1 &&
		nodeEqIgnoring(newNode.firstChild as PMNode, oldNode, ctx.ignoreMarks)
	) {
		return wrapStep(
			/* from   */ pos,
			/* to     */ pos + oldNode.nodeSize,
			/* gapFrom*/ pos,
			/* gapTo  */ pos + oldNode.nodeSize,
			/* shell  */ newNode.type.create(newNode.attrs, null, newNode.marks),
			/* insert */ 1,
		);
	}

	// ---------------------------------------------------------------------------
	// Case 3: unwrap (oldNode has a single child equal to newNode, modulo ignored marks)
	// ---------------------------------------------------------------------------
	if (
		oldNode.childCount === 1 &&
		nodeEqIgnoring(oldNode.firstChild as PMNode, newNode, ctx.ignoreMarks)
	) {
		return new ReplaceAroundStep(
			pos,
			pos + oldNode.nodeSize,
			pos + 1,
			pos + 1 + newNode.nodeSize,
			Slice.empty,
			0,
			/* structure */ true,
		);
	}

	return null;
}

function wrapStep(
	from: number,
	to: number,
	gapFrom: number,
	gapTo: number,
	shell: PMNode,
	insert: number,
): ReplaceAroundStep {
	return new ReplaceAroundStep(
		from,
		to,
		gapFrom,
		gapTo,
		new Slice(Fragment.from(shell), 0, 0),
		insert,
		/* structure */ true,
	);
}
